import { getProgressBar } from '../../progress-bar/executeSteps'
import { NotificationType } from '@wix/editor-platform-sdk-types'
import { truncateCollectionName } from '../truncateCollectionName'
import { getPresetDataForNewCollection } from '../utils/getPresetDataForNewCollection'
import { addPage as addPageWithRepeater } from '../add-collection-and-page-with-repeater/addPage'
import { addItemPage } from '../add-collection-and-dynamic-item-page/addCollectionAndDynamicItemPage'

import {
  createAndPopulatePresetCollection,
  UNDO_REDO_LABELS,
  runTransactionWithRetry,
} from '@wix/wix-data-client-common'
import type {
  Editor3DynamicPagesBiParams,
  AppApi,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { PresetDefinition } from '../../presets/types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { WixDataApi } from '@wix/wix-data-core'
import { generateUniquePageLinkId } from '../../slug/generatePageLinkId'
import type { PageAddResult } from '../add-cms-pages/addCmsPages'
import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'

type Params = {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  i18n: I18n
  fedopsLogger: FedopsLogger
  logger: Logger
  wixDataApi: WixDataApi
  biParams: Editor3DynamicPagesBiParams
}

const interactionName = 'addCollectionAndCmsPages'

export const addCollectionAndCmsPages = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  i18n,
  fedopsLogger,
  logger,
  wixDataApi,
  biParams,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 3,
    title: i18n.t('Editor3_Add_Pages_Progress_Bar_Title_Creating_Pages'),
  })
  await progressBar.open()

  const addPageResults: PageAddResult[] = []

  try {
    const { collectionId, collectionName, schema, records } =
      await getPresetDataForNewCollection({
        preset,
        collectionsApi,
      })

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Setting_Up_Collection',
        {
          collection_name: truncateCollectionName(collectionName),
        },
      ),
    })

    await createAndPopulatePresetCollection({
      collectionsApi,
      collectionId,
      collectionName,
      wixDataApi,
      schema,
      records,
    })

    appApi.refreshCollectionItemCounts([collectionId])

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Adding_List_Page',
        {
          collection_name: truncateCollectionName(collectionName),
        },
      ),
    })

    const pageLinkId = await generateUniquePageLinkId({ editorSdkProxy })
    const createdSchema = await collectionsApi.get(collectionId)

    await runTransactionWithRetry(editorSdkProxy, async () => {
      const listPageResult = await addPageWithRepeater({
        editorSdkProxy,
        preset,
        collectionName,
      })

      addPageResults.push({
        pageRef: listPageResult.pageRef,
        schema: createdSchema,
      })

      await progressBar.update({
        stepTitle: i18n.t(
          'Editor3_Add_Pages_Bundle_Progress_Bar_Step_Title_Adding_Items_Page',
          {
            collection_name: truncateCollectionName(collectionName),
          },
        ),
      })

      const itemPageResult = await addItemPage({
        editorSdkProxy,
        appApi,
        preset,
        collectionsApi,
        collectionId,
        pageLinkId,
      })

      addPageResults.push({
        ...itemPageResult,
        schema: createdSchema,
      })
    })

    await progressBar.update()
  } finally {
    await progressBar.close()
  }

  const [listPageResult, itemPageResult] = addPageResults
  const { pageRef: listPageRef, schema: listPageSchema } = listPageResult
  const {
    pageRef: itemPageRef,
    schema: itemPageSchema,
    newRouterPrefix,
    pattern,
  } = itemPageResult

  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: 'page_with_repeater',
      collection_name: listPageSchema.displayName,
      collection_id: listPageSchema.id,
      is_auto_bound: false,
      new_collection: true,
      pageId: listPageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )

  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: 'item',
      collection_name: itemPageSchema.displayName,
      collection_id: itemPageSchema.id,
      new_url: `${newRouterPrefix}${pattern}`,
      is_auto_bound: false,
      new_collection: true,
      pageId: itemPageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )
  await appApi.handleChangedRouters({ pageRefs: [itemPageRef] })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_ROUTERS,
    params: { pageRefs: [itemPageRef] },
  })

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef: listPageRef })

  appApi.showToastNotification(
    {
      message: i18n.t('Editor3_Add_Pages_Bundle_Toast_Success_Message'),
      type: NotificationType.Success,
      linkText: i18n.t('Editor3_Add_Pages_Bundle_Toast_Success_Link_Text'),
    },
    () => {
      editorSdkProxy.pages.navigateTo({ pageRef: itemPageRef })
    },
  )

  return { listPageRef, itemPageRef }
}
